/**
 * workflowTaskListMerge.ts — Workflow 多 loop 任务合并（§5.11.4）
 */
import type { Task } from '../../types/tasks';
import type { SessionStatus, SessionTree, TaskPhase } from '../../types/tree';
import type { WorkflowSessionState } from '../../types/workflowSession';

import {
  formatTaskFooterLabel,
  shouldShowTaskListFooter,
} from '../TaskListFooter';

export interface WorkflowMergedTask extends Task {
  loopSessionId: string;
}

const STATUS_ORDER: Record<Task['status'], number> = {
  in_progress: 0,
  pending: 1,
  completed: 2,
};

export function mergeWorkflowLoopTasks(
  loopTreesBySessionId: Record<string, SessionTree>,
): WorkflowMergedTask[] {
  const merged: WorkflowMergedTask[] = [];
  const loopIds = Object.keys(loopTreesBySessionId).sort();
  for (const loopSessionId of loopIds) {
    const tree = loopTreesBySessionId[loopSessionId];
    if (!tree) continue;
    for (const task of Object.values(tree.tasks)) {
      merged.push({ ...task, loopSessionId });
    }
  }
  return merged.sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 99;
    const sb = STATUS_ORDER[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    const loopCmp = a.loopSessionId.localeCompare(b.loopSessionId);
    if (loopCmp !== 0) return loopCmp;
    return a.id.localeCompare(b.id);
  });
}

export function deriveWorkflowTaskPhase(trees: SessionTree[]): TaskPhase {
  if (trees.length === 0) return 'hidden';
  if (trees.some((tree) => tree.taskPhase === 'visible')) return 'visible';
  if (trees.every((tree) => tree.taskPhase === 'hidden-after-completion')) {
    return 'hidden-after-completion';
  }
  return 'hidden';
}

export function deriveWorkflowTaskFooterState(state: WorkflowSessionState): {
  tasks: WorkflowMergedTask[];
  taskPhase: TaskPhase;
  hasEverShownTaskList: boolean;
  sessionStatus: SessionStatus;
} {
  const trees = Object.values(state.loopTreesBySessionId);
  return {
    tasks: mergeWorkflowLoopTasks(state.loopTreesBySessionId),
    taskPhase: deriveWorkflowTaskPhase(trees),
    hasEverShownTaskList: trees.some((tree) => tree.hasEverShownTaskList),
    sessionStatus: state.status,
  };
}

export function shouldShowWorkflowTaskListFooter(state: WorkflowSessionState): boolean {
  const { tasks, taskPhase, hasEverShownTaskList, sessionStatus } =
    deriveWorkflowTaskFooterState(state);
  return shouldShowTaskListFooter(taskPhase, tasks, hasEverShownTaskList, sessionStatus);
}

export function formatWorkflowTaskFooterLabel(state: WorkflowSessionState): string {
  const { tasks } = deriveWorkflowTaskFooterState(state);
  return formatTaskFooterLabel(tasks);
}

export function groupWorkflowTasksByLoop(
  tasks: WorkflowMergedTask[],
): Array<{ loopSessionId: string; tasks: Task[] }> {
  const groups = new Map<string, Task[]>();
  for (const task of tasks) {
    const bucket = groups.get(task.loopSessionId) ?? [];
    bucket.push(task);
    groups.set(task.loopSessionId, bucket);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([loopSessionId, groupTasks]) => ({ loopSessionId, tasks: groupTasks }));
}

export function loopSessionDisplayLabel(loopSessionId: string): string {
  const marker = '-';
  const idx = loopSessionId.lastIndexOf(marker);
  if (idx !== -1 && idx < loopSessionId.length - 1) {
    return loopSessionId.slice(idx + 1);
  }
  return loopSessionId;
}
